import React, { useState, useEffect } from "react";
import { Modal, Avatar } from "antd";
import { useDispatch } from "react-redux";
import { bulkUpdateAttendance } from "../../../core/data/redux/employeesAttendanceSlice";
import { formatDateTime } from "../../../utils/dateUtils";
import { toast } from "react-toastify";

interface BulkAttendanceRegularizeModalProps {
  open: boolean;
  items: any[];
  onClose: () => void;
  onSuccess?: () => void;
}

interface ItemCustomConfig {
  actionType?: "accept" | "reject";
  attendanceStatus?: "PRESENT" | "HALF_DAY";
  halfDayType?: string;
  remark?: string;
  checkinLocationMismatch?: boolean;
  checkoutLocationMismatch?: boolean;
  checkinFaceMismatch?: boolean;
  checkoutFaceMismatch?: boolean;
  earlyLeaving?: boolean;
  lateComing?: boolean;
}

const BulkAttendanceRegularizeModal: React.FC<BulkAttendanceRegularizeModalProps> = ({
  open,
  items,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();

  // Selected items list (allows removing items inside modal)
  const [selectedList, setSelectedList] = useState<any[]>([]);

  // Master form state (applied to all selected items by default)
  const [actionType, setActionType] = useState<string>("");
  const [attendanceStatus, setAttendanceStatus] = useState<string>("PRESENT");
  const [halfDayType, setHalfDayType] = useState<string>("");
  const [dayType, setDayType] = useState<string>("");
  const [remarks, setRemarks] = useState<string>("");
  const [remarkError, setRemarkError] = useState<string>("");

  // Options checkboxes for Accept
  const [checkinLocationMismatch, setCheckinLocationMismatch] = useState<boolean>(false);
  const [checkoutLocationMismatch, setCheckoutLocationMismatch] = useState<boolean>(false);
  const [checkinFaceMismatch, setCheckinFaceMismatch] = useState<boolean>(false);
  const [checkoutFaceMismatch, setCheckoutFaceMismatch] = useState<boolean>(false);
  const [earlyLeaving, setEarlyLeaving] = useState<boolean>(false);
  const [lateComing, setLateComing] = useState<boolean>(false);

  // Per-item override configs (optional granular customization)
  const [customConfigs, setCustomConfigs] = useState<Record<number, ItemCustomConfig>>({});
  const [editingItemId, setEditingItemId] = useState<number | null>(null);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Sync selectedList with items prop when modal opens
  useEffect(() => {
    if (open) {
      setSelectedList(items || []);
      setActionType("");
      setAttendanceStatus("PRESENT");
      setHalfDayType("");
      setDayType("");
      setRemarks("");
      setRemarkError("");
      setCheckinLocationMismatch(false);
      setCheckoutLocationMismatch(false);
      setCheckinFaceMismatch(false);
      setCheckoutFaceMismatch(false);
      setEarlyLeaving(false);
      setLateComing(false);
      setCustomConfigs({});
      setEditingItemId(null);
    }
  }, [open, items]);

  const randomAvatarColor = (name: string) => {
    const colors = [
      "#f56a00",
      "#7265e6",
      "#ffbf00",
      "#00a2ae",
      "#87d068",
      "#d46b08",
      "#1890ff",
      "#eb2f96",
    ];
    let hash = 0;
    for (let i = 0; i < (name || "").length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const handleRemoveItem = (idToRemove: number) => {
    const updated = selectedList.filter((item) => (item.id ?? item.key) !== idToRemove);
    setSelectedList(updated);
    if (updated.length === 0) {
      onClose();
    }
  };

  const updateItemConfig = (itemId: number, patch: Partial<ItemCustomConfig>) => {
    setCustomConfigs((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        ...patch,
      },
    }));
  };

  const handleQuickRemark = (text: string) => {
    setRemarks(text);
    setRemarkError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!actionType) {
      toast.warning("Please select an Action Type.");
      return;
    }

    if (actionType === "reject" && !remarks.trim()) {
      setRemarkError("Remark is required for rejection.");
      return;
    }

    if (actionType === "accept" && attendanceStatus === "HALF_DAY" && !halfDayType) {
      toast.warning("Please select Half Day Type.");
      return;
    }

    if (selectedList.length === 0) {
      toast.warning("No records selected to regularize.");
      return;
    }

    // Build requests array matching the cURL specification
    const requests = selectedList.map((item) => {
      const id = item.id ?? item.key;
      const config = customConfigs[id] || {};

      const effectiveAction = config.actionType || actionType;
      const effectiveStatus = config.attendanceStatus || attendanceStatus;
      const effectiveHalfDay = config.halfDayType || halfDayType;
      const effectiveRemark = config.remark !== undefined ? config.remark : remarks;

      if (effectiveAction === "accept") {
        if (effectiveStatus === "HALF_DAY") {
          return {
            id: Number(id),
            is_verified: "VERIFIED",
            attendance_status: "HALF_DAY",
            half_day_type: effectiveHalfDay || "FIRST_HALF",
            regularize_remark: effectiveRemark || "Approved for half day",
          };
        } else {
          const itemCheckinLocMismatch =
            config.checkinLocationMismatch !== undefined
              ? config.checkinLocationMismatch
              : checkinLocationMismatch;
          const itemCheckoutLocMismatch =
            config.checkoutLocationMismatch !== undefined
              ? config.checkoutLocationMismatch
              : checkoutLocationMismatch;
          const itemCheckinFaceMismatch =
            config.checkinFaceMismatch !== undefined
              ? config.checkinFaceMismatch
              : checkinFaceMismatch;
          const itemCheckoutFaceMismatch =
            config.checkoutFaceMismatch !== undefined
              ? config.checkoutFaceMismatch
              : checkoutFaceMismatch;
          const itemEarlyLeaving =
            config.earlyLeaving !== undefined ? config.earlyLeaving : earlyLeaving;
          const itemLateComing =
            config.lateComing !== undefined ? config.lateComing : lateComing;

          const req: any = {
            id: Number(id),
            is_verified: "VERIFIED",
            attendance_status: "PRESENT",
            regularize_remark: effectiveRemark || "Approved - site inspection verified",
            checkin_location_mismatch: Boolean(itemCheckinLocMismatch),
            checkout_location_mismatch: Boolean(itemCheckoutLocMismatch),
            checkin_face_mismatch: Boolean(itemCheckinFaceMismatch),
            checkout_face_mismatch: Boolean(itemCheckoutFaceMismatch),
          };

          if (itemEarlyLeaving) req.early_leaving = "true";
          if (itemLateComing) req.late_coming = "true";

          return req;
        }
      } else {
        // REJECTED
        const req: any = {
          id: Number(id),
          is_verified: "REJECTED",
          regularize_remark: effectiveRemark || "Not approved",
        };
        if (dayType === "HALF_DAY" && halfDayType) {
          req.half_day_type = halfDayType;
        }
        return req;
      }
    });

    console.log("Submitting bulk attendance regularize payload:", { requests });

    try {
      setIsSubmitting(true);
      const res = await dispatch(bulkUpdateAttendance({ requests })).unwrap();
      setIsSubmitting(false);

      const summary = res?.data?.summary || res?.summary;
      const results: any[] = res?.data?.results || res?.results || [];

      if (summary && summary.failed > 0) {
        const failedItems = results.filter((r) => r.status === "FAILED");
        const failedMsg = failedItems
          .map((r) => `ID ${r.id}: ${r.message || "Failed"}`)
          .slice(0, 3)
          .join(" | ");

        if (summary.successful === 0) {
          toast.error(
            `Bulk attendance regularization failed: ${failedMsg}`
          );
        } else {
          toast.warning(
            `${summary.successful} succeeded, ${summary.failed} failed: ${failedMsg}`
          );
        }
      } else {
        toast.success(
          res?.message || `${requests.length} attendance records regularized successfully.`
        );
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err: any) {
      setIsSubmitting(false);
      const msg = typeof err === "string" ? err : err?.message || "Failed to update bulk attendance.";
      toast.error(msg);
    }
  };

  const isFormValid = () => {
    if (!actionType) return false;
    if (actionType === "reject" && !remarks.trim()) return false;
    if (actionType === "accept" && attendanceStatus === "HALF_DAY" && !halfDayType) return false;
    return true;
  };

  return (
    <Modal
      title={
        <div className="d-flex align-items-center justify-content-between pe-4">
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "#FEE2E2",
                color: "#C0282C",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
              }}
            >
              <i className="ti ti-checklist" />
            </div>
            <div>
              <span style={{ fontSize: "17px", fontWeight: "600", color: "#111827" }}>
                Approve Action
              </span>
              <span
                className="badge ms-2"
                style={{
                  backgroundColor: "#E0E7FF",
                  color: "#3730A3",
                  fontSize: "12px",
                  fontWeight: 500,
                  borderRadius: "12px",
                  padding: "4px 8px",
                }}
              >
                Bulk Action ({selectedList.length} Selected)
              </span>
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={false}
      width={820}
      destroyOnClose
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: "calc(90vh - 75px)",
          overflowY: "auto",
          overflowX: "hidden",
          padding: "10px 18px",
        },
      }}
    >
      <div className="row g-3 pt-1">
        {/* Left Column: Selected Employees List */}
        <div className="col-lg-5 col-md-5">
          <div
            style={{
              backgroundColor: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "10px 12px",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>
                Selected Records ({selectedList.length})
              </span>
              <span style={{ fontSize: "11px", color: "#6b7280" }}>
                Pending Approval
              </span>
            </div>

            <div
              style={{
                maxHeight: "260px",
                overflowY: "auto",
                paddingRight: "4px",
              }}
            >
              {selectedList.map((item, idx) => {
                const id = item.id ?? item.key;
                const employee = item.employee;
                const details = employee?.details;
                const first = details?.firstname || "";
                const last = details?.lastname || details?.lsastname || "";
                const fullName = `${first} ${last}`.trim() || `Employee #${item.emp_id || id}`;
                const empId = employee?.empId || item.emp_id || "-";
                const dept =
                  details?.sap_department_designation_wings?.department_name ||
                  details?.department ||
                  "";
                const hasCustom = Boolean(customConfigs[id]);
                const isEditingThis = editingItemId === id;

                return (
                  <div
                    key={id || idx}
                    style={{
                      backgroundColor: isEditingThis ? "#EFF6FF" : "#ffffff",
                      border: isEditingThis
                        ? "1px solid #3B82F6"
                        : "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "10px",
                      marginBottom: "8px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div className="d-flex align-items-start justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        {employee?.sap_employee_images?.image ? (
                          <Avatar
                            src={`data:image/jpeg;base64,${employee.sap_employee_images.image}`}
                            size={36}
                          />
                        ) : (
                          <Avatar
                            style={{
                              backgroundColor: randomAvatarColor(fullName),
                              fontWeight: 600,
                            }}
                            size={36}
                          >
                            {first ? first.charAt(0).toUpperCase() : "E"}
                          </Avatar>
                        )}
                        <div>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#111827",
                              lineHeight: 1.2,
                            }}
                          >
                            {fullName}
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6b7280",
                              marginTop: "2px",
                            }}
                          >
                            <span className="badge bg-light text-dark me-1" style={{ fontSize: "10px" }}>
                              ID: {empId}
                            </span>
                            {dept && <span>{dept}</span>}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-1">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-muted"
                          title="Customize this employee"
                          onClick={() => setEditingItemId(isEditingThis ? null : id)}
                          style={{
                            fontSize: "13px",
                            color: hasCustom ? "#2563EB" : "#6B7280",
                          }}
                        >
                          <i className="ti ti-adjustments" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-danger"
                          title="Remove from bulk list"
                          onClick={() => handleRemoveItem(id)}
                          style={{ fontSize: "14px" }}
                        >
                          <i className="ti ti-x" />
                        </button>
                      </div>
                    </div>

                    {/* Punch details */}
                    <div
                      className="d-flex align-items-center justify-content-between mt-2 pt-2"
                      style={{
                        borderTop: "1px dashed #f3f4f6",
                        fontSize: "11px",
                        color: "#4b5563",
                      }}
                    >
                      <div>
                        <b>In:</b> {item.check_in_time ? formatDateTime(item.check_in_time) : "N/A"}
                      </div>
                      <div className="d-flex gap-1">
                        {item.is_checkin_location === 2 && (
                          <span className="badge bg-danger-subtle text-danger" style={{ fontSize: "10px" }}>
                            Loc Mismatch
                          </span>
                        )}
                        {item.check_in === "L" && (
                          <span className="badge bg-warning-subtle text-warning" style={{ fontSize: "10px" }}>
                            Late
                          </span>
                        )}
                        {item.check_out === "E" && (
                          <span className="badge bg-warning-subtle text-warning" style={{ fontSize: "10px" }}>
                            Early
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Per-item customization collapsible */}
                    {isEditingThis && (
                      <div
                        className="mt-2 pt-2"
                        style={{
                          borderTop: "1px solid #bfdbfe",
                          fontSize: "11px",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <strong style={{ color: "#1e40af" }}>Override for {first || "this item"}:</strong>
                          {hasCustom && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-danger"
                              style={{ fontSize: "10px" }}
                              onClick={() => {
                                const copy = { ...customConfigs };
                                delete copy[id];
                                setCustomConfigs(copy);
                              }}
                            >
                              Reset
                            </button>
                          )}
                        </div>

                        <div className="row g-1">
                          <div className="col-6">
                            <label style={{ fontSize: "10px", color: "#6b7280" }}>Action</label>
                            <select
                              className="form-select form-select-sm"
                              style={{ fontSize: "11px", padding: "2px 6px" }}
                              value={customConfigs[id]?.actionType || actionType || "accept"}
                              onChange={(e) =>
                                updateItemConfig(id, {
                                  actionType: e.target.value as any,
                                })
                              }
                            >
                              <option value="accept">Accept</option>
                              <option value="reject">Reject</option>
                            </select>
                          </div>
                          <div className="col-6">
                            <label style={{ fontSize: "10px", color: "#6b7280" }}>Status</label>
                            <select
                              className="form-select form-select-sm"
                              style={{ fontSize: "11px", padding: "2px 6px" }}
                              value={
                                customConfigs[id]?.attendanceStatus ||
                                attendanceStatus ||
                                "PRESENT"
                              }
                              onChange={(e) =>
                                updateItemConfig(id, {
                                  attendanceStatus: e.target.value as any,
                                })
                              }
                            >
                              <option value="PRESENT">Full Day (Present)</option>
                              <option value="HALF_DAY">Half Day</option>
                            </select>
                          </div>

                          {(customConfigs[id]?.attendanceStatus === "HALF_DAY" ||
                            (!customConfigs[id]?.attendanceStatus &&
                              attendanceStatus === "HALF_DAY")) && (
                            <div className="col-12 mt-1">
                              <label style={{ fontSize: "10px", color: "#6b7280" }}>Half Day Type</label>
                              <select
                                className="form-select form-select-sm"
                                style={{ fontSize: "11px", padding: "2px 6px" }}
                                value={
                                  customConfigs[id]?.halfDayType ||
                                  halfDayType ||
                                  "FIRST_HALF"
                                }
                                onChange={(e) =>
                                  updateItemConfig(id, {
                                    halfDayType: e.target.value,
                                  })
                                }
                              >
                                <option value="FIRST_HALF">First Half</option>
                                <option value="SECOND_HALF">Second Half</option>
                              </select>
                            </div>
                          )}

                          <div className="col-12 mt-1">
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              style={{ fontSize: "11px", padding: "3px 6px" }}
                              placeholder="Specific remark for this employee"
                              value={customConfigs[id]?.remark ?? ""}
                              onChange={(e) =>
                                updateItemConfig(id, { remark: e.target.value })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div
              className="mt-auto pt-2"
              style={{
                borderTop: "1px solid #e5e7eb",
                fontSize: "11px",
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{selectedList.length} records ready</span>
              <span>Bulk settings apply to all</span>
            </div>
          </div>
        </div>

        {/* Right Column: Approval Action Form */}
        <div className="col-lg-7 col-md-7">
          <form onSubmit={handleSubmit}>
            <div className="row g-2">
              {/* Action Type */}
              <div className="col-md-12">
                <label className="form-label" style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}>
                  Action Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  value={actionType}
                  onChange={(e) => {
                    const val = e.target.value;
                    setActionType(val);
                    setRemarkError("");
                    if (val === "reject") {
                      setAttendanceStatus("ABSENT");
                      setRemarks("Not approved");
                    } else if (val === "accept") {
                      setAttendanceStatus("PRESENT");
                      setRemarks("Approved - site inspection verified");
                    }
                  }}
                  style={{ borderRadius: "6px", fontSize: "13px", padding: "5px 10px" }}
                >
                  <option value="">Select Option</option>
                  <option value="accept">Accept</option>
                  <option value="reject">Reject</option>
                </select>
              </div>

              {/* Attendance Status (when Accept) */}
              {actionType === "accept" && (
                <div className="col-md-12">
                  <label className="form-label" style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}>
                    Attendance Status <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={attendanceStatus}
                    onChange={(e) => {
                      setAttendanceStatus(e.target.value);
                      if (e.target.value === "HALF_DAY") {
                        setHalfDayType("FIRST_HALF");
                        setRemarks("Approved for 1st half only");
                      } else {
                        setHalfDayType("");
                        setRemarks("Approved - site inspection verified");
                      }
                    }}
                    style={{ borderRadius: "6px", fontSize: "13px", padding: "5px 10px" }}
                  >
                    <option value="PRESENT">Full Day (Present)</option>
                    <option value="HALF_DAY">Half Day</option>
                  </select>
                </div>
              )}

              {/* Half Day Type (when Accept + Half Day) */}
              {actionType === "accept" && attendanceStatus === "HALF_DAY" && (
                <div className="col-md-12">
                  <label className="form-label" style={{ fontWeight: 600, fontSize: "13px", color: "#374151" }}>
                    Half Day Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={halfDayType}
                    onChange={(e) => {
                      setHalfDayType(e.target.value);
                      if (e.target.value === "FIRST_HALF") {
                        setRemarks("Approved for 1st half only");
                      } else if (e.target.value === "SECOND_HALF") {
                        setRemarks("Approved for 2nd half only");
                      }
                    }}
                    style={{ borderRadius: "6px" }}
                  >
                    <option value="">Select Option</option>
                    <option value="FIRST_HALF">First Half</option>
                    <option value="SECOND_HALF">Second Half</option>
                  </select>
                </div>
              )}

              {/* Options Checkboxes (when Accept + Full Day) */}
              {actionType === "accept" && attendanceStatus === "PRESENT" && (
                <div className="col-md-12">
                  <label
                    className="form-label"
                    style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}
                  >
                    Options
                  </label>
                  <div
                    style={{
                      backgroundColor: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      padding: "8px 10px",
                    }}
                  >
                    <div className="row g-2">
                      <div className="col-sm-6">
                        <div className="form-check d-flex align-items-center gap-2 m-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="bulk_checkinLocationMismatch"
                            checked={checkinLocationMismatch}
                            onChange={(e) => setCheckinLocationMismatch(e.target.checked)}
                            style={{ width: "16px", height: "16px", cursor: "pointer" }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="bulk_checkinLocationMismatch"
                            style={{ fontSize: "12px", cursor: "pointer", userSelect: "none" }}
                          >
                            Check-in Location Mismatch
                          </label>
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <div className="form-check d-flex align-items-center gap-2 m-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="bulk_checkoutLocationMismatch"
                            checked={checkoutLocationMismatch}
                            onChange={(e) => setCheckoutLocationMismatch(e.target.checked)}
                            style={{ width: "16px", height: "16px", cursor: "pointer" }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="bulk_checkoutLocationMismatch"
                            style={{ fontSize: "12px", cursor: "pointer", userSelect: "none" }}
                          >
                            Check-out Location Mismatch
                          </label>
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <div className="form-check d-flex align-items-center gap-2 m-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="bulk_checkinFaceMismatch"
                            checked={checkinFaceMismatch}
                            onChange={(e) => setCheckinFaceMismatch(e.target.checked)}
                            style={{ width: "16px", height: "16px", cursor: "pointer" }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="bulk_checkinFaceMismatch"
                            style={{ fontSize: "12px", cursor: "pointer", userSelect: "none" }}
                          >
                            Check-in Face Mismatch
                          </label>
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <div className="form-check d-flex align-items-center gap-2 m-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="bulk_checkoutFaceMismatch"
                            checked={checkoutFaceMismatch}
                            onChange={(e) => setCheckoutFaceMismatch(e.target.checked)}
                            style={{ width: "16px", height: "16px", cursor: "pointer" }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="bulk_checkoutFaceMismatch"
                            style={{ fontSize: "12px", cursor: "pointer", userSelect: "none" }}
                          >
                            Check-out Face Mismatch
                          </label>
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <div className="form-check d-flex align-items-center gap-2 m-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="bulk_earlyLeaving"
                            checked={earlyLeaving}
                            onChange={(e) => setEarlyLeaving(e.target.checked)}
                            style={{ width: "16px", height: "16px", cursor: "pointer" }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="bulk_earlyLeaving"
                            style={{ fontSize: "12px", cursor: "pointer", userSelect: "none" }}
                          >
                            Early Leaving
                          </label>
                        </div>
                      </div>

                      <div className="col-sm-6">
                        <div className="form-check d-flex align-items-center gap-2 m-0">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="bulk_lateComing"
                            checked={lateComing}
                            onChange={(e) => setLateComing(e.target.checked)}
                            style={{ width: "16px", height: "16px", cursor: "pointer" }}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="bulk_lateComing"
                            style={{ fontSize: "12px", cursor: "pointer", userSelect: "none" }}
                          >
                            Late Coming
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Day Type (when Reject) */}
              {actionType === "reject" && (
                <div className="col-md-12">
                  <label className="form-label" style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}>
                    Day Type
                  </label>
                  <select
                    className="form-select"
                    value={dayType}
                    onChange={(e) => {
                      setDayType(e.target.value);
                      if (e.target.value === "FULL_DAY") {
                        setHalfDayType("");
                      }
                    }}
                    style={{ borderRadius: "6px", fontSize: "13px", padding: "5px 10px" }}
                  >
                    <option value="">Select Option</option>
                    <option value="FULL_DAY">Full Day</option>
                    <option value="HALF_DAY">Half Day</option>
                  </select>
                </div>
              )}

              {/* Half Day Type (when Reject + Half Day) */}
              {actionType === "reject" && dayType === "HALF_DAY" && (
                <div className="col-md-12">
                  <label className="form-label" style={{ fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "2px" }}>
                    Half Day Type
                  </label>
                  <select
                    className="form-select"
                    value={halfDayType}
                    onChange={(e) => setHalfDayType(e.target.value)}
                    style={{ borderRadius: "6px", fontSize: "13px", padding: "5px 10px" }}
                  >
                    <option value="">Select Option</option>
                    <option value="FIRST_HALF">First Half</option>
                    <option value="SECOND_HALF">Second Half</option>
                  </select>
                </div>
              )}

              {/* Remarks */}
              <div className="col-md-12">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="form-label m-0" style={{ fontWeight: 600, fontSize: "12px", color: "#374151" }}>
                    Remarks {actionType === "reject" && <span className="text-danger">*</span>}
                  </label>
                  <span style={{ fontSize: "11px", color: "#6b7280" }}>
                    Applies to all selected
                  </span>
                </div>
                <textarea
                  className="form-control"
                  placeholder="Remarks"
                  rows={2}
                  value={remarks}
                  onChange={(e) => {
                    setRemarks(e.target.value);
                    if (e.target.value.trim()) setRemarkError("");
                  }}
                  style={{ borderRadius: "6px", fontSize: "13px" }}
                />
                {remarkError && (
                  <small className="text-danger d-block mt-1">{remarkError}</small>
                )}

                {/* Quick Suggestion Chips */}
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {actionType === "accept" && (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickRemark("Approved - site inspection verified")}
                      >
                        Site inspection verified
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickRemark("Approved as per duty roster")}
                      >
                        As per roster
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickRemark("Approved by Controlling Officer")}
                      >
                        Approved by CO
                      </button>
                    </>
                  )}
                  {actionType === "reject" && (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickRemark("Not approved")}
                      >
                        Not approved
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickRemark("Attendance mismatch not justified")}
                      >
                        Mismatch not justified
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger py-0 px-2"
                        style={{ fontSize: "11px", borderRadius: "12px" }}
                        onClick={() => handleQuickRemark("Punch record irregular")}
                      >
                        Record irregular
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="col-md-12 pt-2">
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-light px-3"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn px-4 d-flex align-items-center gap-2"
                    disabled={!isFormValid() || isSubmitting || selectedList.length === 0}
                    style={{
                      backgroundColor: "#C0282C",
                      borderColor: "#C0282C",
                      color: "#fff",
                      fontSize: "15px",
                      fontWeight: "500",
                      borderRadius: "6px",
                      opacity: !isFormValid() || isSubmitting || selectedList.length === 0 ? 0.6 : 1,
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" />
                        Regularizing...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-check" />
                        Regularize ({selectedList.length})
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default BulkAttendanceRegularizeModal;
